import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import api from "@/shared/api/api";
import { PaginatedResponse } from "@/features/profiles/services/profiles/types";

export interface Category {
    id?: string;
    name: string;
    type: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
}

export interface CreateCategoryPayload {
    name: string;
    type: string;
}

export interface UpdateCategoryPayload {
    name: string;
    type: string;
}

class CategoryService implements IService {
    [key: string]: ActionKeyPair<unknown, unknown>;

    /**
     * Busca todas as categorias via GET /v1/categories com paginação
     */
    getCategories: ActionKeyPair<{ page?: number; limit?: number; type?: "PRESET" | "LEARNING" | "" }, PaginatedResponse<Category>> = {
        key: "get-categories",
        actionBuilder: ({ page = 1, limit, type = "" } = {}) => async () => {
            const params = new URLSearchParams();
            if (page) params.append('page', page.toString());
            if (limit) params.append('limit', limit.toString());
            if (type) params.append('type', type);

            const url = "/v1/categories";

            const response = await api<{ data: Category[], meta: { totalPages: number, total: number, page: number, limit: number } }>({
                url,
                method: "GET" as Method,
                params,
            });

            return {
                totalPage: response.meta.totalPages,
                totalItems: response.meta.total,
                page: response.meta.page,
                limit: response.meta.limit,
                data: response.data,
            };
        },
    };

    /**
     * Busca uma categoria específica via GET /v1/categories/:id
     */
    getCategoryById: ActionKeyPair<{ id: string }, Category> = {
        key: "get-category-by-id",
        actionBuilder: ({ id }) => async () => {
            return await api({
                url: `/v1/categories/${id}`,
                method: "GET" as Method,
            });
        },
    };

    /**
     * Cria uma nova categoria via POST /v1/categories
     */
    createCategory: ActionKeyPair<CreateCategoryPayload, Category> = {
        key: "create-category",
        actionBuilder: (data) => async () => {
            return await api({
                url: "/v1/categories",
                method: "POST" as Method,
                data,
            });
        },
    };

    /**
     * Atualiza uma categoria via PUT /v1/categories/:id
     */
    updateCategory: ActionKeyPair<{ id: string; data: UpdateCategoryPayload }, Category> = {
        key: "update-category",
        actionBuilder: ({ id, data }) => async () => {
            return await api({
                url: `/v1/categories/${id}`,
                method: "PATCH" as Method,
                data,
            });
        },
    };

    /**
     * Deleta uma categoria via DELETE /v1/categories/:id
     */
    deleteCategory: ActionKeyPair<{ id: string }, void> = {
        key: "delete-category",
        actionBuilder: ({ id }) => async () => {
            await api({
                url: `/v1/categories/${id}`,
                method: "DELETE" as Method,
            });
        },
    };

    /**
     * Associa um preset a uma categoria via POST /v1/categories/:id/presets
     */
    associatePresetToCategory: ActionKeyPair<{ categoryId: string; presetId: string }, { id: string }> = {
        key: "associate-preset-to-category",
        actionBuilder: ({ categoryId, presetId }) => async () => {
            return await api({
                url: `/v1/categories/${categoryId}/presets`,
                method: "POST" as Method,
                data: { presetId },
            });
        },
    };

    /**
     * Remove um preset de uma categoria via DELETE /v1/categories/:id/presets/:id
     */
    removePresetFromCategory: ActionKeyPair<{ categoryId: string; presetId: string }, void> = {
        key: "remove-preset-from-category",
        actionBuilder: ({ categoryId, presetId }) => async () => {
            await api({
                url: `/v1/categories/${categoryId}/presets/${presetId}`,
                method: "DELETE" as Method,
            });
        },
    };

    static Instance = new CategoryService();
}

export default CategoryService;